<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <card-page>
          <vue-element-loading :active="busy"></vue-element-loading>
          <vue-form-generator
              ref="form"
              :model="model"
              :options="formOptions"
              :schema="schema"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>

import FormModelMixin from '@/components/mixins/FormModelMixin';
import CardPage from '@/views/components/Views/CardPage';
import {selectOptions, datepickerOptions, arrayOptions} from '@/components/form/FormElementOption';
import AdditiveWidget from '@/components/form/fields/AdditiveWidget';
import FormFieldValidationMixin from '@/components/mixins/FormFieldValidationMixin';

export default {
  name: 'ProductForm',
  components: {
    CardPage,
    AdditiveWidget,
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    cardTitle: {
      type: String,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      disableEntities: false,
      pathValues: {
        level1: null,
        level2: null,
        level3: null,
      },
      model: {
        Description: null,
        Parent_CommercialProductNode_FK: null,
        Localization_FK: null,
        Name: null,
        Path: null,
        Image: null,
        Tag: null,
        Level: null,
        IsDeleted: false,
        IsEnabled: false,
        Organization: null,
        Location: null,
        Size: null,
      },
      schema: {
        groups: [
          {
            legend: 'General',
            styleClasses: 'w-full',
            fields: [
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Name',
                model: 'Name',
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Description',
                model: 'Description',
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Parent Node',
                model: 'Parent_CommercialProductNode_FK',
                values: () => this.parentNodeOptions,
                selectOptions,
              },
              {
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Localisation',
                model: 'Localization_FK',
                values: () => this.localisationOptions,
                selectOptions,
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Path',
                model: 'Path',
                disabled: true,
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Image',
                model: 'Image',
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'vx-col w-1/3',
                label: 'Tag',
                model: 'Tag',
              },
              {
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Level',
                model: 'Level',
                values: [1,2,3],
                selectOptions: {
                  showLabels: false,
                  hideSelected: false,
                },
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                type: 'vsCheckbox',
                label: 'Is Deleted',
                styleClasses: 'w-full',
                model: 'IsDeleted',
              },
              {
                type: 'vsCheckbox',
                label: 'Is Enabled',
                styleClasses: 'w-full',
                model: 'IsEnabled',
              },
              {
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Organisation',
                model: 'Organization',
                values: ['FUW', 'IPR'],
                selectOptions: {
                  showLabels: false,
                  hideSelected: false,
                },
              },
              {
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Location',
                model: 'Location',
                validator: [(value) => this.validateOptionalNumericRange(value)],
              },
              {
                type: 'input',
                inputType: 'number',
                styleClasses: 'vx-col w-1/3',
                label: 'Size',
                model: 'Size',
                validator: [(value) => this.validateOptionalNumericRange(value)],
              },
            ]
          },
        ],
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true,
      },
      parentNodeOptions: [],
      localisationOptions: [],
      options: [
        {
          endpoint: () => this.$api.commercialProducts.nodesList(),
          optionsFieldName: "parentNodeOptions",
          nameFieldName: "name",
          idFieldName: 'Parent_CommercialProductNode_FK',
          additionalFields: ['parentNodeId', 'grandParentNodeId'],
        },
        {
          endpoint: () => this.$api.contentManagementNodes.localizationOptions(),
          optionsFieldName: "localisationOptions",
          nameFieldName: "name",
          idFieldName: 'Localization_FK',
        },
      ]
    };
  },
  watch: {
    'model.Location'(newValue) {
      if (newValue === 'NaN' || isNaN(newValue)) {
        this.model.Location = null;
      }
    },
    'model.Size'(newValue) {
      if (newValue === 'NaN' || isNaN(newValue)) {
        this.model.Size = null;
      }
    },
    'model.Parent_CommercialProductNode_FK'(newValue) {
      if (newValue) {
        this.pathValues.level1 = newValue.id;
        this.pathValues.level2 = newValue.parentNodeId;
        this.pathValues.level3 = newValue.grandParentNodeId;
        this.setPath();
      }
    },
    'model.Level'(newValue) {
      if (newValue) {
        this.setPath();
      }
    }
  },
  methods: {
    setPath() {
      let pathParts = [];
      
      if (this.model.Level === 1) {
        pathParts = [this.pathValues.level1];
      } else if (this.model.Level === 2) {
        pathParts = [this.pathValues.level1, this.pathValues.level2];
      } else if (this.model.Level === 3) {
        pathParts = [this.pathValues.level1, this.pathValues.level2, this.pathValues.level3];
      }
      
      this.model.Path = pathParts.filter(part => part !== null && part !== undefined).join('|');
    },
    validateOptionalNumericRange(value, maxValue = 255) {
      if (value === null || value === undefined || value === '' || value === 'NaN' || value === 'nan') {
        return true;
      }
      
      // Convert to number and check if it's valid
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return true;
      }
      
      if (numValue < 0) {
        return ['Value must be positive'];
      }
      
      return true;
    }
  },
};
</script>
